import { KeyboardEvent, Suspense, lazy } from "react";
import { Popover } from "@headlessui/react";
import { EmojiStyle } from "emoji-picker-react";
import { HiOutlineFaceSmile, HiOutlineMagnifyingGlass, HiOutlineXMark } from "react-icons/hi2";
import { IRibbonColors, RibbonPosition } from "@/api_framework/api_modals/HomeTileConfig";
import {
  RIBBON_POSITION_CELLS,
  RIBBON_POSITION_LABEL,
  RIBBON_TEXT_SUGGESTIONS,
  RibbonTextSuggestion,
  sectionLabelClassName,
} from "./interface";

export const ClearInputButton = ({
  label,
  onClear,
  className = "right-2",
}: {
  label: string;
  onClear: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClear}
    className={`absolute top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-slate-200 text-slate-600 shadow-sm transition-colors hover:bg-rose-100 hover:text-rose-600 ${className}`}
    aria-label={label}
  >
    <HiOutlineXMark className="h-3 w-3" strokeWidth={2.5} />
  </button>
);

const SIZE_CLASSNAME = {
  md: {
    wrapper: "relative",
    icon: "left-3 h-4 w-4",
    input: "h-9 w-[260px] rounded-lg pl-9",
    clear: "right-2",
  },
  sm: {
    wrapper: "relative w-full max-w-[420px] flex-1",
    icon: "left-2.5 h-3.5 w-3.5",
    input: "h-8 w-full rounded-md pl-8",
    clear: "right-1.5",
  },
};

export const SearchInput = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  clearLabel,
  size = "md",
  onKeyDown,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  ariaLabel: string;
  clearLabel: string;
  size?: keyof typeof SIZE_CLASSNAME;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}) => {
  const classNames = SIZE_CLASSNAME[size];
  return (
    <div className={classNames.wrapper}>
      <HiOutlineMagnifyingGlass
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${classNames.icon}`}
      />
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={`border border-slate-200 bg-white pr-9 text-xs text-slate-700 shadow-sm focus:border-[#193A6A] focus:outline-none ${classNames.input}`}
      />
      {value.length > 0 && (
        <ClearInputButton label={clearLabel} onClear={() => onChange("")} className={classNames.clear} />
      )}
    </div>
  );
};

const EmojiPicker = lazy(() => import("emoji-picker-react"));

export const RibbonTextSuggestions = ({
  disabled,
  onSelect,
  onInsertEmoji,
  selectedStyle,
  showEmojiPicker = true,
}: {
  disabled: boolean;
  onSelect: (suggestion: RibbonTextSuggestion) => void;
  onInsertEmoji?: (emoji: string) => void;
  selectedStyle?: IRibbonColors;
  showEmojiPicker?: boolean;
}) => (
  <div className="mt-2">
    <span className={`block ${sectionLabelClassName}`}>Select ribbon format</span>
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
    {RIBBON_TEXT_SUGGESTIONS.map((suggestion) => {
      const isSelected =
        Boolean(selectedStyle) &&
        selectedStyle?.bg_color.toLowerCase() === suggestion.bg_color.toLowerCase() &&
        selectedStyle?.text_color.toLowerCase() === suggestion.text_color.toLowerCase() &&
        selectedStyle?.border_color.toLowerCase() === suggestion.border_color.toLowerCase();
      return (
        <button
          aria-pressed={isSelected}
          key={suggestion.text}
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelect(suggestion)}
          title={`Use "${suggestion.text}"`}
          style={{
            backgroundColor: suggestion.bg_color,
            color: suggestion.text_color,
            borderColor: suggestion.border_color,
          }}
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold shadow-sm transition hover:brightness-95 ${
            isSelected ? "ring-2 ring-[#193A6A] ring-offset-1" : ""
          }`}
        >
          {suggestion.text}
        </button>
      );
    })}
    {showEmojiPicker && (
    <Popover className="relative">
      <Popover.Button
        type="button"
        onMouseDown={(event) => event.preventDefault()}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#193A6A]/40 bg-white px-2 py-0.5 text-[11px] font-semibold text-[#193A6A] transition-colors hover:bg-[#EEF5FF] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <HiOutlineFaceSmile className="h-3.5 w-3.5" />
        More emojis
      </Popover.Button>
      <Popover.Panel className="absolute left-0 z-30 mt-2 rounded-xl shadow-xl">
        <Suspense
          fallback={
            <div className="flex h-[400px] w-[320px] items-center justify-center rounded-xl border border-slate-200 bg-white text-[11px] text-slate-500">
              Loading emojis…
            </div>
          }
        >
          <EmojiPicker
            onEmojiClick={(emoji) => {
              if (!disabled) onInsertEmoji?.(emoji?.emoji);
            }}
            emojiStyle={EmojiStyle.NATIVE}
            previewConfig={{ showPreview: false }}
            searchPlaceHolder="Search emoji"
            width={320}
            height={400}
          />
        </Suspense>
      </Popover.Panel>
    </Popover>
    )}
    </div>
  </div>
);

export const RibbonPositionPicker = ({
  value,
  onChange,
}: {
  value: RibbonPosition;
  onChange: (next: RibbonPosition) => void;
}) => {
  const cellClassName = (isSelected: boolean) =>
    `flex h-8 items-center justify-center rounded-md border transition-colors ${
      isSelected
        ? "border-[#193A6A] bg-[#EEF5FF]"
        : "border-slate-200 bg-white hover:border-[#193A6A]/40 hover:bg-slate-50"
    }`;

  const renderBanner = (position: "top_full_width" | "full_width") => (
    <button
      type="button"
      title={RIBBON_POSITION_LABEL[position]}
      aria-label={RIBBON_POSITION_LABEL[position]}
      aria-pressed={value === position}
      onClick={() => onChange(position)}
      className={`flex h-8 items-center justify-center gap-2 rounded-xl border px-2 text-[11px] font-medium transition-colors ${
        value === position
          ? "border-[#193A6A] bg-[#EEF5FF] text-[#193A6A]"
          : "border-slate-200 bg-white text-slate-500 hover:border-[#193A6A]/40 hover:bg-slate-50"
      }`}
    >
      <span className={`h-1.5 w-8 rounded-full ${value === position ? "bg-[#193A6A]" : "bg-slate-300"}`} />
      {RIBBON_POSITION_LABEL[position]}
    </button>
  );

  return (
    <div className="inline-flex w-[168px] flex-col gap-1.5">
      {renderBanner("top_full_width")}
      <div className="grid grid-cols-3 gap-1.5 rounded-xl border border-slate-200 bg-slate-50 p-1.5">
        {RIBBON_POSITION_CELLS.map((position) => (
          <button
            key={position}
            type="button"
            title={RIBBON_POSITION_LABEL[position]}
            aria-label={RIBBON_POSITION_LABEL[position]}
            aria-pressed={value === position}
            onClick={() => onChange(position)}
            className={cellClassName(value === position)}
          >
            <span className={`h-1.5 w-5 rounded-full ${value === position ? "bg-[#193A6A]" : "bg-slate-300"}`} />
          </button>
        ))}
      </div>
      {renderBanner("full_width")}
      <span className="text-[11px] text-slate-500">{RIBBON_POSITION_LABEL[value]}</span>
    </div>
  );
};

export const SegmentedControl = <TValue extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label?: string;
  options: { key: TValue; label: string }[];
  value: TValue;
  onChange: (next: TValue) => void;
}) => (
  <div className="flex items-center gap-2">
    {label && <span className="text-xs font-medium text-slate-500">{label}</span>}
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => onChange(option.key)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
            value === option.key ? "bg-white text-[#193A6A] shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

export const StatusSwitch = ({
  checked,
  onRequestChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onRequestChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onRequestChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#193A6A]/30 disabled:cursor-not-allowed disabled:opacity-60 ${
      checked ? "bg-[#193A6A]" : "bg-slate-200"
    }`}
  >
    <span
      aria-hidden="true"
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);
